// Single source of truth for all POD platforms supported by Profit Lab.
// Every module (NicheResearch, TagGenerator, EarningsCalculator, MultiPlatformKit,
// platformExporter) MUST read specs/SEO/commission data from here.

export type PlatformId =
  | "redbubble"
  | "teepublic"
  | "society6"
  | "zazzle"
  | "spreadshirt"
  | "threadless"
  | "merch"
  | "etsy"
  | "displate";

export type PlatformScope = PlatformId | "all";

export type CommissionModel =
  | { type: "percentage"; rate: number; note?: string }
  | { type: "flat"; usd: number; note?: string }
  | { type: "range"; min: number; max: number; note?: string }
  | { type: "free"; note: string };

export interface ImageSpec {
  width: number;
  height: number;
  dpi: number;
  transparency: boolean;
  format: "PNG" | "JPG";
  maxFileSizeMB?: number;
}

export interface SeoRules {
  titleMaxChars: number;
  tagsCount: number;
  tagMaxChars?: number;
  descriptionStyle: "keyword-dense" | "storytelling" | "bullet-list" | "concise";
  descriptionMaxChars: number;
}

export interface ProductCatalogItem {
  id: string;
  label: string;
  basePriceUsd: number;
}

export interface PlatformConfig {
  id: PlatformId;
  name: string;
  /** HSL string used as a CSS color value (no `hsl()` wrapper). */
  brandColorHsl: string;
  uploaderUrl: string;
  imageSpec: ImageSpec;
  seoRules: SeoRules;
  commission: CommissionModel;
  productCatalog: ProductCatalogItem[];
  audience: { primary: string; regions: string[] };
  difficulty: "low" | "medium" | "high";
  /** Lower = onboard first. */
  priorityRank: number;
  shortPitch: string;
}

export const PLATFORMS: Record<PlatformId, PlatformConfig> = {
  redbubble: {
    id: "redbubble",
    name: "Redbubble",
    brandColorHsl: "0 84% 55%",
    uploaderUrl: "https://www.redbubble.com/portfolio/images/new",
    imageSpec: { width: 5000, height: 5000, dpi: 300, transparency: true, format: "PNG", maxFileSizeMB: 20 },
    seoRules: { titleMaxChars: 50, tagsCount: 15, tagMaxChars: 25, descriptionStyle: "keyword-dense", descriptionMaxChars: 600 },
    commission: { type: "percentage", rate: 0.2, note: "20% default markup, fully editable" },
    productCatalog: [
      { id: "tshirt", label: "Classic T-Shirt", basePriceUsd: 19.84 },
      { id: "sticker", label: "Sticker", basePriceUsd: 2.47 },
      { id: "poster", label: "Poster", basePriceUsd: 14.79 },
      { id: "hoodie", label: "Pullover Hoodie", basePriceUsd: 39.58 },
      { id: "mug", label: "Classic Mug", basePriceUsd: 14.93 },
    ],
    audience: { primary: "Gen Z & millennials, fandom + niche humor", regions: ["US", "UK", "AU", "DE"] },
    difficulty: "low",
    priorityRank: 1,
    shortPitch: "Largest POD marketplace. Easiest to start, lowest margins.",
  },
  teepublic: {
    id: "teepublic",
    name: "TeePublic",
    brandColorHsl: "204 70% 53%",
    uploaderUrl: "https://www.teepublic.com/design/new",
    imageSpec: { width: 2400, height: 3200, dpi: 150, transparency: true, format: "PNG", maxFileSizeMB: 10 },
    seoRules: { titleMaxChars: 50, tagsCount: 13, tagMaxChars: 30, descriptionStyle: "concise", descriptionMaxChars: 500 },
    commission: { type: "flat", usd: 4, note: "$4 base royalty per t-shirt sale" },
    productCatalog: [
      { id: "tshirt", label: "T-Shirt", basePriceUsd: 20 },
      { id: "sticker", label: "Sticker", basePriceUsd: 3 },
      { id: "hoodie", label: "Hoodie", basePriceUsd: 40 },
    ],
    audience: { primary: "Pop-culture fans, gamers", regions: ["US", "UK"] },
    difficulty: "low",
    priorityRank: 2,
    shortPitch: "Owned by Redbubble. Same artwork, different audience.",
  },
  society6: {
    id: "society6",
    name: "Society6",
    brandColorHsl: "175 65% 45%",
    uploaderUrl: "https://society6.com/studio",
    imageSpec: { width: 6500, height: 6500, dpi: 300, transparency: false, format: "PNG", maxFileSizeMB: 50 },
    seoRules: { titleMaxChars: 60, tagsCount: 10, tagMaxChars: 20, descriptionStyle: "storytelling", descriptionMaxChars: 1000 },
    commission: { type: "percentage", rate: 0.1, note: "10% on most products, 10% above base on art prints" },
    productCatalog: [
      { id: "art-print", label: "Art Print", basePriceUsd: 22 },
      { id: "throw-pillow", label: "Throw Pillow", basePriceUsd: 28 },
      { id: "duvet", label: "Duvet Cover", basePriceUsd: 129 },
      { id: "tapestry", label: "Tapestry", basePriceUsd: 69 },
    ],
    audience: { primary: "Home-decor buyers, premium aesthetic", regions: ["US", "EU"] },
    difficulty: "medium",
    priorityRank: 4,
    shortPitch: "Premium home decor. Higher AOV, lower commission.",
  },
  zazzle: {
    id: "zazzle",
    name: "Zazzle",
    brandColorHsl: "200 90% 50%",
    uploaderUrl: "https://www.zazzle.com/create",
    imageSpec: { width: 3000, height: 3000, dpi: 300, transparency: true, format: "PNG", maxFileSizeMB: 25 },
    seoRules: { titleMaxChars: 70, tagsCount: 10, tagMaxChars: 25, descriptionStyle: "keyword-dense", descriptionMaxChars: 800 },
    commission: { type: "range", min: 0.05, max: 0.99, note: "Royalty 5–99% set by you (10–15% recommended)" },
    productCatalog: [
      { id: "invitation", label: "Invitation", basePriceUsd: 2.4 },
      { id: "business-card", label: "Business Card", basePriceUsd: 25 },
      { id: "mug", label: "Mug", basePriceUsd: 16 },
      { id: "tshirt", label: "T-Shirt", basePriceUsd: 22 },
    ],
    audience: { primary: "Personalization & events buyers", regions: ["US"] },
    difficulty: "medium",
    priorityRank: 6,
    shortPitch: "Best for customizable products & invitations. You set royalty.",
  },
  spreadshirt: {
    id: "spreadshirt",
    name: "Spreadshirt",
    brandColorHsl: "150 70% 40%",
    uploaderUrl: "https://www.spreadshirt.com/account/designs/new",
    imageSpec: { width: 4000, height: 4000, dpi: 300, transparency: true, format: "PNG", maxFileSizeMB: 10 },
    seoRules: { titleMaxChars: 50, tagsCount: 8, tagMaxChars: 20, descriptionStyle: "concise", descriptionMaxChars: 500 },
    commission: { type: "percentage", rate: 0.2, note: "Marketplace ~20% design commission" },
    productCatalog: [
      { id: "tshirt", label: "T-Shirt", basePriceUsd: 18 },
      { id: "hoodie", label: "Hoodie", basePriceUsd: 42 },
      { id: "tank", label: "Tank Top", basePriceUsd: 16 },
    ],
    audience: { primary: "European buyers, quality apparel", regions: ["DE", "EU", "UK"] },
    difficulty: "medium",
    priorityRank: 5,
    shortPitch: "European powerhouse. Best for EU-targeted apparel niches.",
  },
  threadless: {
    id: "threadless",
    name: "Threadless",
    brandColorHsl: "10 80% 55%",
    uploaderUrl: "https://www.threadless.com/dashboard/uploads/new",
    imageSpec: { width: 1800, height: 2400, dpi: 150, transparency: true, format: "PNG", maxFileSizeMB: 15 },
    seoRules: { titleMaxChars: 50, tagsCount: 10, tagMaxChars: 25, descriptionStyle: "storytelling", descriptionMaxChars: 600 },
    commission: { type: "range", min: 1, max: 20, note: "Add $1–$20 markup per item" },
    productCatalog: [
      { id: "tshirt", label: "T-Shirt", basePriceUsd: 22 },
      { id: "hoodie", label: "Hoodie", basePriceUsd: 45 },
      { id: "art-print", label: "Art Print", basePriceUsd: 18 },
    ],
    audience: { primary: "Artists & illustration fans", regions: ["US", "EU"] },
    difficulty: "medium",
    priorityRank: 7,
    shortPitch: "Artist community + Artist Shops. Curated vibe, loyal buyers.",
  },
  merch: {
    id: "merch",
    name: "Amazon Merch on Demand",
    brandColorHsl: "35 95% 55%",
    uploaderUrl: "https://merch.amazon.com",
    imageSpec: { width: 4500, height: 5400, dpi: 300, transparency: true, format: "PNG", maxFileSizeMB: 25 },
    seoRules: { titleMaxChars: 60, tagsCount: 5, tagMaxChars: 50, descriptionStyle: "bullet-list", descriptionMaxChars: 2000 },
    commission: { type: "range", min: 0.13, max: 0.37, note: "13–37% royalty depending on price tier" },
    productCatalog: [
      { id: "tshirt", label: "Standard T-Shirt", basePriceUsd: 19.99 },
      { id: "long-sleeve", label: "Long Sleeve", basePriceUsd: 24.99 },
      { id: "hoodie", label: "Hoodie", basePriceUsd: 39.99 },
      { id: "popsocket", label: "PopSocket", basePriceUsd: 14.99 },
    ],
    audience: { primary: "Amazon mass-market shoppers", regions: ["US", "UK", "DE", "JP"] },
    difficulty: "high",
    priorityRank: 3,
    shortPitch: "Highest traffic + royalties. Application required, slow tier-up.",
  },
  etsy: {
    id: "etsy",
    name: "Etsy (POD via Printful/Printify)",
    brandColorHsl: "20 90% 55%",
    uploaderUrl: "https://www.etsy.com/your/shops/me/tools/listings/new",
    imageSpec: { width: 4500, height: 5400, dpi: 300, transparency: true, format: "PNG", maxFileSizeMB: 20 },
    seoRules: { titleMaxChars: 140, tagsCount: 13, tagMaxChars: 20, descriptionStyle: "keyword-dense", descriptionMaxChars: 5000 },
    commission: { type: "free", note: "You set price. Fees: $0.20 listing + 6.5% transaction + payment fees" },
    productCatalog: [
      { id: "tshirt", label: "T-Shirt (Printful)", basePriceUsd: 12.95 },
      { id: "sweatshirt", label: "Sweatshirt", basePriceUsd: 22.95 },
      { id: "poster", label: "Poster", basePriceUsd: 8.95 },
      { id: "mug", label: "Mug", basePriceUsd: 7.95 },
    ],
    audience: { primary: "Gift buyers, niche & personalized shoppers", regions: ["US", "UK", "EU", "AU"] },
    difficulty: "high",
    priorityRank: 8,
    shortPitch: "Highest margins (you set price). Requires Printful/Printify integration.",
  },
  displate: {
    id: "displate",
    name: "Displate",
    brandColorHsl: "260 70% 60%",
    uploaderUrl: "https://displate.com/artist-panel/upload",
    imageSpec: { width: 2960, height: 4137, dpi: 150, transparency: false, format: "PNG", maxFileSizeMB: 50 },
    seoRules: { titleMaxChars: 60, tagsCount: 10, tagMaxChars: 25, descriptionStyle: "concise", descriptionMaxChars: 500 },
    commission: { type: "range", min: 1.5, max: 4.5, note: "Royalty scales by sales volume tier" },
    productCatalog: [
      { id: "displate-m", label: "Displate (M)", basePriceUsd: 39 },
      { id: "displate-l", label: "Displate (L)", basePriceUsd: 65 },
      { id: "displate-xl", label: "Displate (XL)", basePriceUsd: 99 },
    ],
    audience: { primary: "Gamers, fandom collectors, premium decor", regions: ["EU", "US"] },
    difficulty: "medium",
    priorityRank: 9,
    shortPitch: "Premium metal posters. Fandom + gaming niches dominate.",
  },
};

export const PLATFORM_LIST: PlatformConfig[] = Object.values(PLATFORMS).sort(
  (a, b) => a.priorityRank - b.priorityRank
);

export const PLATFORM_IDS: PlatformId[] = PLATFORM_LIST.map((p) => p.id);

export function getPlatform(id: PlatformId): PlatformConfig {
  return PLATFORMS[id];
}

export function isPlatformId(value: string): value is PlatformId {
  return value in PLATFORMS;
}

/**
 * Estimate royalty per sale in USD given a sell price.
 * For percentage models: rate * price.
 * For flat: fixed.
 * For range: midpoint of range applied to price (or absolute for Threadless/Displate).
 * For free (Etsy): price - base - fees (rough).
 */
export function estimateRoyalty(platform: PlatformConfig, sellPriceUsd: number, basePriceUsd?: number): number {
  const c = platform.commission;
  switch (c.type) {
    case "percentage":
      return +(sellPriceUsd * c.rate).toFixed(2);
    case "flat":
      return c.usd;
    case "range": {
      // Zazzle/Merch use percentages; Threadless/Displate use absolute markups.
      if (c.max <= 1) {
        const mid = (c.min + c.max) / 2;
        return +(sellPriceUsd * mid).toFixed(2);
      }
      const mid = (c.min + c.max) / 2;
      return +mid.toFixed(2);
    }
    case "free": {
      const base = basePriceUsd ?? 0;
      const listingFee = 0.2;
      const txFee = sellPriceUsd * 0.065;
      return +(sellPriceUsd - base - listingFee - txFee).toFixed(2);
    }
  }
}
